// mobile-source — lógica PURA do fluxo "configurar fonte" mobile (parede A6).
//
// Mesmo padrão de mobile-shell-view.ts / mobile-catalog.ts: funções puras,
// testáveis sem DOM. A apresentação (MobileConfigureSource) só LIGA estas
// funções; nada de React/rede aqui.
//
// A fonte da conta Swipe é cadastrada pelo MESMO endpoint do site
// (PUT /v1/swipe-account/preference): CÓDIGO do provedor + usuário/senha do
// painel Xtream (+ URL opcional do servidor). O backend valida contra o painel
// e, validando, libera o trial pleno na hora (regra I0). Não há caminho de
// "lista pessoal sem código" na conta Swipe — por isso a detecção de URL colada
// no campo do código (abaixo) orienta o usuário a usar o código de acesso.
//
// ATUALIZAÇÃO (14/08): o parágrafo acima ficou parcialmente obsoleto — existe sim
// o caminho de "lista pessoal sem código" na conta Swipe (`sourceType:'manual'`,
// ver `manual-source-form.ts`). O que continua valendo é a regra DESTE formulário,
// que é o de código: URL colada no campo do código segue sendo erro orientado.

import { isHttpUrl } from '../url-shape'

/** Campos crus do formulário (strings do input nativo). */
export interface SourceFormInput {
  /** Código do provedor (4-8 chars no backend) — normalizado p/ MAIÚSCULAS. */
  providerCode: string
  /** Usuário do painel do provedor (Xtream). */
  xtreamUsername: string
  /** Senha do painel do provedor (Xtream). */
  xtreamPassword: string
  /** URL do servidor Xtream (opcional, metadado). */
  serverUrl: string
}

/** Erro de validação local por campo (i18n key) — antes de ir ao servidor. */
export interface SourceFormErrors {
  providerCode?: string
  xtreamUsername?: string
  xtreamPassword?: string
  serverUrl?: string
  /** Erro de topo (ex.: colou URL no campo do código). */
  form?: string
}

/** Parâmetros já prontos pra api.setSwipePreference (serverUrl só quando há). */
export interface SourcePreferenceParams {
  providerCode: string
  xtreamUsername: string
  xtreamPassword: string
  serverUrl?: string | undefined
}

/** True quando o texto parece uma URL (http/https ou host:porta colado). Usado
 *  pra detectar que o usuário colou o ENDEREÇO do servidor no campo do CÓDIGO,
 *  e orientar a usar o código de acesso (a conta Swipe é sempre por código). */
export function looksLikeUrl(value: string): boolean {
  const v = value.trim().toLowerCase()
  if (!v) return false
  if (v.startsWith('http://') || v.startsWith('https://')) return true
  // host:porta ou domínio com ponto + barra (ex.: meuserver.com:8080)
  if (/^[a-z0-9.-]+:\d{2,5}(\/|$)/.test(v)) return true
  if (/^[a-z0-9-]+\.[a-z]{2,}([/:]|$)/.test(v)) return true
  return false
}

/** Normaliza a URL do servidor pra salvar: trim. Vazio => undefined (campo
 *  opcional). Não força protocolo aqui (o backend valida o formato). */
export function normalizeServerUrl(value: string): string | undefined {
  const v = value.trim()
  return v.length > 0 ? v : undefined
}

/**
 * Valida a URL informada NESTE formulário, onde ela é OPCIONAL: vazio é válido;
 * preenchida, precisa ser http(s) absoluta.
 *
 * A política do vazio é a única coisa que mora aqui — a FORMA vem de
 * `url-shape.ts`, dono único (antes havia três cópias da mesma checagem, duas
 * delas com este mesmo nome e regras contrárias pro vazio).
 */
export function isValidServerUrl(value: string): boolean {
  if (!value.trim()) return true
  return isHttpUrl(value)
}

/**
 * Valida o formulário e devolve os erros por campo (i18n keys) OU os params
 * prontos pra chamada. Regras:
 *  - código: 4-8 chars (bate com o backend SwipePreferenceBody). Se o usuário
 *    colou uma URL no campo do código, devolve erro de topo orientando o código
 *    (reusa a mensagem de "provedor cadastrado, use o código").
 *  - usuário/senha do painel: obrigatórios.
 *  - URL do servidor (opcional): se preenchida, precisa ser http(s) válida.
 */
export function validateSourceForm(
  input: SourceFormInput,
): { ok: true; params: SourcePreferenceParams } | { ok: false; errors: SourceFormErrors } {
  const errors: SourceFormErrors = {}
  const code = input.providerCode.trim().toUpperCase()
  const username = input.xtreamUsername.trim()
  const password = input.xtreamPassword

  if (looksLikeUrl(input.providerCode)) {
    // Colou o endereço do servidor onde se espera o código. A conta Swipe é
    // por código; reusa a copy de "use o código de acesso".
    errors.form = 'errors.endpointBelongsToProvider'
    errors.providerCode = 'mobileSource.error.codeNotUrl'
  } else if (code.length < 4 || code.length > 8) {
    errors.providerCode = 'mobileSource.error.codeFormat'
  }
  if (username.length < 1) errors.xtreamUsername = 'mobileSource.error.userRequired'
  if (password.length < 1) errors.xtreamPassword = 'mobileSource.error.passwordRequired'
  if (!isValidServerUrl(input.serverUrl)) errors.serverUrl = 'mobileSource.error.urlInvalid'

  if (
    errors.providerCode ||
    errors.xtreamUsername ||
    errors.xtreamPassword ||
    errors.serverUrl ||
    errors.form
  ) {
    return { ok: false, errors }
  }

  const params: SourcePreferenceParams = {
    providerCode: code,
    xtreamUsername: username,
    xtreamPassword: password,
  }
  const url = normalizeServerUrl(input.serverUrl)
  if (url) params.serverUrl = url
  return { ok: true, params }
}

/** Resultado da chamada ao backend traduzido pra estado da UI. `validated` =
 *  fonte validada na hora (trial pleno); senão salvou best-effort (Xtream
 *  fora do ar) — sucesso, mas com aviso. */
export interface SourceSaveOutcome {
  status: 'validated' | 'saved-unverified'
}

/** Mapeia a resposta do PUT preference (api.setSwipePreference) pro estado da
 *  UI. `validated:true` => validado; senão salvo sem verificar agora. */
export function outcomeFromResponse(res: {
  validated: boolean
  validationSkippedReason: string | null
}): SourceSaveOutcome {
  return { status: res.validated ? 'validated' : 'saved-unverified' }
}
